import { useCallback, useRef, type RefObject } from "react";

interface UseEditorSelectionParams {
  editorRef: RefObject<HTMLDivElement | null>;
}

export const useEditorSelection = ({ editorRef }: UseEditorSelectionParams) => {
  const savedSelectionRef = useRef<Range | null>(null);

  const restoreSelection = useCallback(() => {
    if (!editorRef.current) return;

    editorRef.current.focus();
    const selection = window.getSelection();
    if (!selection) return;

    selection.removeAllRanges();
    if (savedSelectionRef.current) {
      selection.addRange(savedSelectionRef.current);
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(editorRef.current);
    range.collapse(false);
    selection.addRange(range);
  }, [editorRef]);

  const saveCurrentSelection = useCallback(() => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (editorRef.current.contains(range.commonAncestorContainer)) {
      savedSelectionRef.current = range.cloneRange();
    }
  }, [editorRef]);

  return {
    savedSelectionRef,
    restoreSelection,
    saveCurrentSelection,
  };
};
